import { useAuth } from '@clerk/react';
import { useQuery } from '@tanstack/react-query';
import { useInjection } from 'inversify-react';
import { useCallback, useMemo } from 'react';
import { PermissionService } from '../services/permission/permission.service';
import type { IPermissionService } from '../services/permission/permission.service.interface';
import { useValidatedTenantId } from './auth/use-validated-tenant-id';

const PERMISSIONS_ME_ROOT = ['permissions', 'me'] as const;

export const permissionQueryKeys = {
  meRoot: () => PERMISSIONS_ME_ROOT,
  me: (tenantId: string | null | undefined) => [...PERMISSIONS_ME_ROOT, tenantId] as const,
};

const FIVE_MINUTES = 5 * 60 * 1000;

// Permissions the backend withholds from the Owner role (see backend
// permission.service.ts OWNER_PERMISSIONS). The Owner super-user shortcut must
// NOT re-grant these on the frontend. DOCUMENT_GENERATION is Consultant-only.
const OWNER_EXCLUDED_PERMISSIONS = new Set<string>(['DOCUMENT_GENERATION']);

/**
 * Owner-aware permission check. The Owner is a super-user for every permission
 * except the ones the backend deliberately withholds from the Owner role
 * (currently DOCUMENT_GENERATION) — for those we trust the backend-sent array
 * so the frontend cannot re-grant what the backend excluded.
 */
export function ownerAwareHasPermission(
  isOwner: boolean,
  permissions: readonly string[],
  permission: string,
): boolean {
  if (isOwner && !OWNER_EXCLUDED_PERMISSIONS.has(permission)) {
    return true;
  }
  return permissions.includes(permission);
}

export function usePermissions() {
  const permissionService = useInjection<IPermissionService>(PermissionService);
  const { isSignedIn } = useAuth();
  const currentTenantId = useValidatedTenantId();

  const { data, isLoading, isPending, fetchStatus, isError } = useQuery({
    queryKey: permissionQueryKeys.me(currentTenantId),
    queryFn: () => permissionService.getMyPermissions(),
    staleTime: FIVE_MINUTES,
    refetchOnWindowFocus: true,
    enabled: !!isSignedIn && !!currentTenantId,
  });

  const permissions = useMemo(() => data?.permissions ?? [], [data?.permissions]);
  const readOnlyPermissions = useMemo(
    () => data?.readOnlyPermissions ?? [],
    [data?.readOnlyPermissions],
  );
  const isOwner = data?.isOwner ?? false;
  const isReadOnly = data?.isReadOnly ?? false;
  const isPlatformAdmin = data?.isPlatformAdmin ?? false;
  const roleName = data?.roleName ?? null;
  const docGenerationBetaEnabled = data?.docGenerationBetaEnabled ?? false;

  const hasPermission = useCallback(
    (permission: string): boolean => ownerAwareHasPermission(isOwner, permissions, permission),
    [permissions, isOwner],
  );

  const hasAnyPermission = useCallback(
    (...requiredPermissions: string[]): boolean =>
      requiredPermissions.some((p) => ownerAwareHasPermission(isOwner, permissions, p)),
    [permissions, isOwner],
  );

  const isPermissionReadOnly = useCallback(
    (permission: string): boolean => {
      return readOnlyPermissions.includes(permission);
    },
    [readOnlyPermissions],
  );

  return {
    permissions,
    isLoading,
    isPending,
    // Disambiguates a permanently-disabled query (`fetchStatus: 'idle'`) from
    // one actually in flight — see `isGuardQuerySettling` in
    // `router/guards/guard-query-state.ts`.
    fetchStatus,
    isError,
    isOwner,
    isReadOnly,
    isPlatformAdmin,
    roleName,
    docGenerationBetaEnabled,
    hasPermission,
    hasAnyPermission,
    isPermissionReadOnly,
  };
}
